import React, { memo, useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/ui/_refactor/popup/modal';
import {
  useNotificationModalRootActionsStore,
  useNotificationModalRootStore,
} from '../notification-modal-root/notification-modal-root.store';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import {
  useTicketingConfigurationNotificationQuery,
  GET_TICKETING_CONFIGURATION_NOTIFICATION_QUERY_KEY,
} from '../../../../notifications/api/get-ticketing-configuration-notification.api';
import { GET_LIST_SPACE_CONFIG_QUERY_KEY } from '../../../api/get-list-space-config.api';
import {
  useUpdateTicketingConfigurationNotificationMutation,
  UpdateTicketingConfigurationNotificationValidationSchema,
} from '../../../api/update-ticketing-configuration-notification.api';
import type { TUpdateTicketingConfigurationNotificationValidation } from '../../../api/update-ticketing-configuration-notification.api';
import { TTicketingConfigurationNotification } from '../../../types/notification.type';
import { queryClient } from '@/lib/react-query';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';

const notificationConfig: NotificationConfig[] = [
  // Ticket
  {
    key: 'TICKET_CREATED',
    label: 'New Ticket',
    description: 'Get notified when a new ticket is created.',
    channels: ['email', 'app'],
  },
  {
    key: 'TICKET_UPDATED',
    label: 'Ticket Updated',
    description: 'Get notified when a ticket is updated.',
    channels: ['app'],
  },
  {
    key: 'TICKET_MOVED',
    label: 'Ticket Moved',
    description: 'Get notified when a ticket is moved to a different column.',
    channels: ['app'],
  },
  {
    key: 'TICKET_COMPLETED',
    label: 'Ticket Completed',
    description: 'Get notified when a ticket is marked as completed.',
    channels: ['email', 'app'],
  },
  // Mention
  {
    key: 'TICKET_MENTIONED_USER',
    label: 'Ticket Mention',
    description: 'Get notified when you are mentioned in a ticket.',
    channels: ['email', 'app'],
  },
  {
    key: 'COMMENT_MENTIONED_USER',
    label: 'Mention in Comment',
    description: 'Get notified when you are mentioned in a comment.',
    channels: ['email', 'app'],
  },
  {
    key: 'TICKET_REMOVED_USER',
    label: 'Mention Removed',
    description: 'Get notified when your mention is removed from a ticket.',
    channels: ['email', 'app'],
  },
  // Comment
  {
    key: 'COMMENT_ADDED',
    label: 'New Comment',
    description: 'Get notified when a new comment is added to a ticket.',
    channels: ['app'],
  },
  // Column
  {
    key: 'COLUMN_ADDED',
    label: 'New Column',
    description: 'Get notified when a new column is created in the space.',
    channels: ['app'],
  },
  {
    key: 'COLUMN_DELETED',
    label: 'Column Deleted',
    description: 'Get notified when a column is deleted from the space.',
    channels: ['app'],
  },
  {
    key: 'COLUMN_RENAMED',
    label: 'Column Renamed',
    description: 'Get notified when a column is renamed.',
    channels: ['app'],
  },
  // Space
  {
    key: 'SPACE_ADDED_USER',
    label: 'Added to Space',
    description: 'Get notified when you are added to a space.',
    channels: ['email', 'app'],
  },
  {
    key: 'SPACE_REMOVED_USER',
    label: 'Removed from Space',
    description: 'Get notified when you are removed from a space.',
    channels: ['email', 'app'],
  },
  // Reminder
  {
    key: 'REMINDER_NOTIFICATION',
    label: 'Reminder Ticket Due Date',
    description: 'Get notified when a ticket is approaching its due date.',
    channels: ['email', 'app'],
  },
  // Role
  {
    key: 'UPDATE_ROLE',
    label: 'Role Change',
    description: 'Get notified when your role in a space is changed.',
    channels: ['email', 'app'],
  },
];

const getChangedFields = (
  original: TUpdateTicketingConfigurationNotificationValidation | null,
  current: TUpdateTicketingConfigurationNotificationValidation
): Partial<Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>> | null => {
  if (!original) return null;

  const changes: Partial<Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>> = {};

  (
    Object.keys(current).filter((key) => key !== 'spaceId') as Array<
      keyof Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>
    >
  ).forEach((key) => {
    const originalConfig = original[key];
    const currentConfig = current[key];

    // Skip if original doesn't have this config or if configs are not objects
    if (!originalConfig || !currentConfig || typeof originalConfig !== 'object' || typeof currentConfig !== 'object')
      return;

    // Check if any value changed
    const hasChanged = (Object.keys(currentConfig) as Array<keyof typeof currentConfig>).some(
      (field) => originalConfig[field] !== currentConfig[field]
    );

    if (hasChanged) {
      changes[key] = currentConfig;
    }
  });

  return Object.keys(changes).length > 0 ? changes : null;
};

// Pure function to check if all notification configs are selected
const isAllSelected = (configs: TUpdateTicketingConfigurationNotificationValidation): boolean => {
  const keys = Object.keys(configs).filter((key) => key !== 'spaceId') as Array<
    keyof Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>
  >;

  if (keys.length === 0) return false;

  return keys.every((key) => {
    const config = configs[key];
    if (!config || typeof config === 'string') return false;

    // Check all available channels (app and/or email) are true
    const hasApp = 'app' in config;
    const hasEmail = 'email' in config;

    if (hasApp && hasEmail) {
      return config.app === true && config.email === true;
    } else if (hasApp) {
      return config.app === true;
    } else if (hasEmail) {
      return config.email === true;
    }

    return false;
  });
};

// Pure function to update all notification configs
const updateAllNotificationConfigs = (
  configs: TUpdateTicketingConfigurationNotificationValidation,
  checked: boolean
): Partial<Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>> => {
  const updated: Partial<Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>> = {};

  (
    Object.keys(configs).filter((key) => key !== 'spaceId') as Array<
      keyof Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>
    >
  ).forEach((key) => {
    const config = configs[key];
    if (config && typeof config === 'object') {
      const updatedConfig = { ...config };
      if ('app' in updatedConfig) {
        updatedConfig.app = checked;
      }
      if ('email' in updatedConfig) {
        updatedConfig.email = checked;
      }
      updated[key] = updatedConfig;
    }
  });

  return updated;
};

export type TUpdateTicketingNotificationModalType = 'UpdateTicketingNotificationModal';
export interface IUpdateTicketingNotificationModalProps {
  spaceId: string;
}

// Types
interface NotificationConfig {
  key: keyof TTicketingConfigurationNotification['config'];
  label: string;
  description: string;
  channels: ('app' | 'email')[];
}

const UpdateTicketingNotificationModal = memo(({ spaceId }: IUpdateTicketingNotificationModalProps) => {
  const showToast = useCustomToast();
  const notificationModalRootActions = useNotificationModalRootActionsStore();

  const isOpen = useNotificationModalRootStore(
    useShallow((state) => state.modals.some((m) => m.type === 'UpdateTicketingNotificationModal'))
  );

  const handleClose = useCallback(() => {
    notificationModalRootActions.closeModal('UpdateTicketingNotificationModal');
  }, [notificationModalRootActions]);

  // Fetch server data when modal opens
  const { data: serverData } = useTicketingConfigurationNotificationQuery({
    params: { spaceId },
    queryConfig: { enabled: isOpen && !!spaceId },
  });

  // Mutation hook
  const { mutate: updateNotification, isPending } = useUpdateTicketingConfigurationNotificationMutation({
    mutationConfigOptions: {
      onSuccess: () => {
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_TICKETING_CONFIGURATION_NOTIFICATION_QUERY_KEY, { spaceId }],
          }),
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_SPACE_CONFIG_QUERY_KEY],
          }),
        ]);
        showToast({
          message: 'Notification updated successfully!',
          type: 'success',
        });
        handleClose();
      },
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error updating notification, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
    },
  });

  const { handleSubmit, watch, setValue, reset } = useForm<TUpdateTicketingConfigurationNotificationValidation>({
    resolver: zodResolver(UpdateTicketingConfigurationNotificationValidationSchema),
    defaultValues: {
      spaceId,
      UPDATE_ROLE: { app: false, email: false },
      COLUMN_ADDED: { app: false },
      TICKET_MOVED: { app: false },
      COMMENT_ADDED: { app: false },
      COLUMN_DELETED: { app: false },
      COLUMN_RENAMED: { app: false },
      TICKET_CREATED: { app: false, email: false },
      TICKET_UPDATED: { app: false },
      SPACE_ADDED_USER: { app: false, email: false },
      TICKET_COMPLETED: { app: false, email: false },
      SPACE_REMOVED_USER: { app: false, email: false },
      TICKET_REMOVED_USER: { app: false, email: false },
      REMINDER_NOTIFICATION: { app: false, email: false },
      TICKET_MENTIONED_USER: { app: false, email: false },
      COMMENT_MENTIONED_USER: { app: false, email: false },
    },
  });

  // Reference to store the initial server data
  const [initialServerData, setInitialServerData] =
    useState<TUpdateTicketingConfigurationNotificationValidation | null>(null);

  // Sync form with server data when it arrives
  useEffect(() => {
    if (serverData?.payload?.config) {
      // Store the initial server data
      const serverConfig = {
        ...serverData.payload.config,
        spaceId,
      } as TUpdateTicketingConfigurationNotificationValidation;
      setInitialServerData(serverConfig);
      // Reset form with server data
      reset(serverConfig);
    }
  }, [serverData, reset, spaceId]);

  const watchAll = watch() as TUpdateTicketingConfigurationNotificationValidation;
  const allSelected = isAllSelected(watchAll);

  // Function to get only changed fields compared to server data

  const onSubmit = (data: TUpdateTicketingConfigurationNotificationValidation) => {
    const changedData = getChangedFields(initialServerData, data);

    if (changedData) {
      updateNotification({
        ...changedData,
        spaceId,
      });
    } else {
      handleClose();
    }
  };

  // Handle Select All checkbox
  const handleSelectAll = (checked: boolean) => {
    const configs = watchAll;
    const updatedConfigs = updateAllNotificationConfigs(configs, checked);

    (
      Object.keys(updatedConfigs) as Array<keyof Omit<TUpdateTicketingConfigurationNotificationValidation, 'spaceId'>>
    ).forEach((key) => {
      setValue(key, updatedConfigs[key]!);
    });
  };

  // RENDER
  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalHeader>
        <ModalTitle>Manage Notification Ticketing</ModalTitle>
        <ModalDescription>
          Select which notification types you want to enable for this ticketing space.
        </ModalDescription>
      </ModalHeader>
      <ModalContent>
        {/* Select All */}
        <div className="flex items-center ">
          <label className="flex items-center cursor-pointer gap-3">
            <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
            <span className="text-sm font-medium text-gray-700">Select All</span>
          </label>
        </div>

        {/* Notification Items */}
        <div className="mt-[16px]">
          {notificationConfig.map((item) => (
            <div key={item.key} className="flex items-center justify-between py-[8px] border-b border-gray-200">
              <div className="flex-1">
                <h3 className="text-[12px] font-[400] text-[#25151E]">{item.label}</h3>
                <p className="text-[10px] font-[400] text-[#9D9D9C] mt-[4px]">{item.description}</p>
              </div>
              <div className="flex items-center gap-4 ml-[48px]">
                {item.channels.includes('email') && (
                  <label className="flex items-center cursor-pointer gap-2">
                    <Checkbox
                      checked={
                        (
                          watch(item.key as keyof TUpdateTicketingConfigurationNotificationValidation) as {
                            email?: boolean;
                          }
                        )?.email || false
                      }
                      onCheckedChange={(checked) => {
                        const currentConfig = watch(
                          item.key as keyof TUpdateTicketingConfigurationNotificationValidation
                        ) as { app?: boolean; email?: boolean } | undefined;
                        setValue(
                          item.key as keyof TUpdateTicketingConfigurationNotificationValidation,
                          {
                            ...currentConfig,
                            email: checked === true,
                          } as TUpdateTicketingConfigurationNotificationValidation[keyof TUpdateTicketingConfigurationNotificationValidation],
                          { shouldValidate: true }
                        );
                      }}
                    />
                    <span className="text-[10px] font-[700] cursor-pointer text-[#9D9D9C]">Email</span>
                  </label>
                )}
                {item.channels.includes('app') && (
                  <label className="flex items-center cursor-pointer gap-2">
                    <Checkbox
                      checked={
                        (
                          watch(item.key as keyof TUpdateTicketingConfigurationNotificationValidation) as {
                            app?: boolean;
                          }
                        )?.app || false
                      }
                      onCheckedChange={(checked) => {
                        const currentConfig = watch(
                          item.key as keyof TUpdateTicketingConfigurationNotificationValidation
                        ) as { app?: boolean; email?: boolean } | undefined;
                        setValue(
                          item.key as keyof TUpdateTicketingConfigurationNotificationValidation,
                          {
                            ...currentConfig,
                            app: checked === true,
                          } as TUpdateTicketingConfigurationNotificationValidation[keyof TUpdateTicketingConfigurationNotificationValidation],
                          { shouldValidate: true }
                        );
                      }}
                    />
                    <span className="text-[10px] font-[700] cursor-pointer text-[#9D9D9C]">App</span>
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalConfirmButton className="px-[24px]" onClick={handleSubmit(onSubmit)} disabled={isPending}>
          {isPending ? 'Saving...' : 'Save'}
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

UpdateTicketingNotificationModal.displayName = 'UpdateTicketingNotificationModal';
export default UpdateTicketingNotificationModal;
